import React, { createContext, useContext, useEffect, useState } from 'react';

import { useSessionContext } from '../contexts/SessionContext';
import useCopilot from '../hooks/useCopilot';
import useKnowledgeLibrary from '../hooks/useKnowledgeLibrary';
import useSearch from '../hooks/useSearch';

import {
  ICopilot,
  ISpeakers,
  ISpeakerSegments,
  ITranscriptSegment,
  ITranscriptSentence,
} from '../global/interfaces';

import { IViewDocument, IFormData } from '@homeporter/types';
const defaultState: ICopilot = {
  filterTranscriptContext:
    'A homeowner is describing a problem they have with the laminate floors in their home to a home inspector.',
  setFilterTranscriptContext: () => {},

  searchResults: [],
  clearSearchResults: () => {},
  searchLoading: false,
  searchError: false,
  updateSentence: () => {},

  copilotEnabled: false,
  setCopilotEnabled: () => {},

  aiMode: '',
  setAiMode: () => {},

  filterHomeSystems: false,
  setFilterHomeSystems: () => {},

  scoreThreshold: 0.5,
  setScoreThreshold: () => {},

  selectedSentenceId: '',
  setSelectedSentenceId: () => {},

  searchEnqueue: () => {},

  storeTranscriptEmbeddingsLoading: false,
  storeTranscriptEmbeddingsError: false,

  documentLoading: false,
  documentError: false,
  clearError: () => {},
  updateDocument: async () => {
    throw new Error('updateDocument must be used inside CopilotProvider');
  },
  bookList: [],
  manualList: [],
  checklistList: [],
  formList: [],
  setFormList: () => {
    throw new Error('setFormList must be used within a CopilotProvider');
  },
  refetchDocuments: () => {},
  viewDocument: null,
  setViewDocument: (_: IViewDocument | null) => {},
  setDocumentParagraphQueue: () => {},
  documentParagraphMapRef: { current: null },

  transcriptSentenceMapRef: { current: null },
  transcriptSearchResultsMapRef: { current: null },
  transcriptSentenceEmbeddingsQueueMapRef: { current: null },
  classifyTranscriptResultsMapRef: { current: null },

  filterTranscriptResultsMap: new Map(),
  setFilterTranscriptResultsMap: () => {},

  setStoreTranscriptEmbeddingsQueue: () => {},

  filterTranscriptPrompt: '',
  setFilterTranscriptPrompt: () => {},
  filterTranscriptResult: () => {},
  filterTranscriptLoading: false,
  filterTranscriptError: false,

  filterModelList: [],
  filterModel: 0,
  setFilterModel: () => {},

  homeSystemOptions: [],
  selectedClassifierOptions: [],
  setSelectedClassifierOptions: () => [],

  applyClassifier: [],
  setApplyClassifier: () => {},

  filterTranscriptWithDecoder: () => {},
  isFilteringTranscript: 0,
  errorFilteringTranscript: false,
  setErrorFilteringTranscript: () => {},

  storeFilterResults: () => {},
  filterTranscriptResultsStore: [],
  setFilterTranscriptResultsStore: () => {},
};

const CopilotContext = createContext(defaultState);

export function CopilotProvider({ children }: { children: JSX.Element }) {
  const [copilotEnabled, setCopilotEnabled] = useState<boolean>(false);
  const { booking, transcriptSegments } = useSessionContext();

  const {
    loading: documentLoading,
    error: documentError,
    clearError,
    updateDocument,
    bookList,
    manualList,
    checklistList,
    formList,
    setFormList,
    refetchDocuments,
    setDocumentParagraphQueue,
    documentParagraphMapRef,
    viewDocument,
    setViewDocument,
  } = useKnowledgeLibrary();

  const {
    searchLoading,
    searchError,
    searchResults,
    clearSearchResults,
    updateSentence,

    selectedSentenceId,
    setSelectedSentenceId,

    searchEnqueue,
    transcriptSearchResultsMapRef,
  } = useSearch(documentParagraphMapRef.current);

  const {
    filterTranscriptContext,
    setFilterTranscriptContext,

    aiMode,
    setAiMode,

    transcriptSentenceMapRef,

    filterHomeSystems,
    setFilterHomeSystems,
    scoreThreshold,
    setScoreThreshold,

    storeTranscriptEmbeddingsLoading,
    storeTranscriptEmbeddingsError,

    setStoreTranscriptEmbeddingsQueue,
    setSpeakerTranscript,

    transcriptSentenceEmbeddingsQueueMapRef,
    classifyTranscriptResultsMapRef,

    filterTranscriptResultsMap,
    setFilterTranscriptResultsMap,

    filterTranscriptPrompt,
    setFilterTranscriptPrompt,
    filterTranscriptResult,
    filterTranscriptLoading,
    filterTranscriptError,

    filterModelList,
    filterModel,
    setFilterModel,

    homeSystemOptions,
    selectedClassifierOptions,
    setSelectedClassifierOptions,

    applyClassifier,
    setApplyClassifier,

    filterTranscriptWithDecoder,
    isFilteringTranscript,
    errorFilteringTranscript,
    setErrorFilteringTranscript,

    storeFilterResults,
    filterTranscriptResultsStore,
    setFilterTranscriptResultsStore,
  } = useCopilot(String(booking?.booking_id), searchEnqueue);

  useEffect(() => {
    console.log('reset Maps for copilot');

    transcriptSearchResultsMapRef.current = new Map();
    documentParagraphMapRef.current = new Map();
    transcriptSentenceMapRef.current = new Map();
    transcriptSentenceEmbeddingsQueueMapRef.current = new Map();
    classifyTranscriptResultsMapRef.current = new Map();

    setFilterTranscriptResultsMap(new Map());
    setFilterTranscriptResultsStore([]);

    setSpeakerTranscript({ speakers: { uid: '' }, segments: [] });
  }, [booking]);

  //this will tell ai server to create and store embeddings for new sentences that are not in classifyTranscriptResultsMapRef
  useEffect(() => {
    if (
      (!copilotEnabled &&
        !selectedClassifierOptions.length &&
        !filterTranscriptPrompt &&
        !searchResults) ||
      filterModel > 0
    ) {
      return;
    }

    console.log(
      '::::inside useEffect copilot setStoreTranscriptEmbeddingsQueue...',
    );
    const transcriptSentenceEmbeddingsQueue: ITranscriptSentence[] = [];

    transcriptSegments?.forEach(
      (segment: ITranscriptSegment, index: number) => {
        if (!segment?.sentences) return;

        segment.sentences.forEach((sentence: ITranscriptSentence) => {
          if (
            !transcriptSentenceEmbeddingsQueueMapRef?.current?.has(sentence.sid)
          ) {
            transcriptSentenceEmbeddingsQueue.push(sentence);
          }
        });
      },
    );

    if (transcriptSentenceEmbeddingsQueue.length > 0) {
      console.log(
        'append new transcriptSegments with copilotEnabled',
        transcriptSentenceEmbeddingsQueue,
      );

      setStoreTranscriptEmbeddingsQueue((prev: ITranscriptSentence[]) => [
        ...prev,
        ...transcriptSentenceEmbeddingsQueue,
      ]);
    } else {
      console.log('nothing to do...');
    }
  }, [
    transcriptSegments,
    copilotEnabled,
    filterModel,
    selectedClassifierOptions,
  ]);

  useEffect(() => {
    if (!copilotEnabled || filterModel === 0) {
      return;
    }
    console.log(
      '::::inside useEffect copilot setSpeakerTranscript, will do semantic chunking here...',
    );

    const speakers: ISpeakers = { uid: '' };
    const segments: ISpeakerSegments[] = [];

    transcriptSegments?.forEach((part: ITranscriptSegment, index: number) => {
      if (!part?.user_id || !part?.user_name || !part?.sentences) return;

      if (!speakers[part.user_id]) {
        speakers[part.user_id] = part.user_name;
      }

      segments.push({
        uid: part.user_id,
        sids: part.sentences.map((item) => item.sid),
      });
    });

    console.log('setSpeakerTranscript:', {
      speakers,
      sentences: segments,
    });

    setSpeakerTranscript({
      speakers,
      segments,
    });
  }, [transcriptSegments, copilotEnabled, filterModel]);

  useEffect(() => {
    if (!copilotEnabled) return;

    transcriptSegments.forEach((part: ITranscriptSegment) => {
      part.sentences?.forEach((sentence, index) => {
        transcriptSentenceMapRef.current.set(sentence.sid, sentence);
      });
    });

    setFilterTranscriptResultsStore([]);
    console.log('useEffect clear setFilterTranscriptResultsMap...');
    setFilterTranscriptResultsMap(new Map());
  }, [transcriptSegments, copilotEnabled]);

  return (
    <CopilotContext.Provider
      value={{
        filterTranscriptContext,
        setFilterTranscriptContext,

        searchResults,
        clearSearchResults,
        searchLoading,
        searchError,
        updateSentence,

        copilotEnabled,
        setCopilotEnabled,

        aiMode,
        setAiMode,

        filterHomeSystems,
        setFilterHomeSystems,
        scoreThreshold,
        setScoreThreshold,

        selectedSentenceId,
        setSelectedSentenceId,

        searchEnqueue,

        storeTranscriptEmbeddingsLoading,
        storeTranscriptEmbeddingsError,

        documentLoading,
        documentError,
        clearError,
        updateDocument,
        bookList,
        manualList,
        checklistList,
        formList,
        setFormList,
        refetchDocuments,
        viewDocument,
        setViewDocument,
        setDocumentParagraphQueue,
        documentParagraphMapRef,
        transcriptSentenceMapRef,
        transcriptSearchResultsMapRef,
        transcriptSentenceEmbeddingsQueueMapRef,
        classifyTranscriptResultsMapRef,

        filterTranscriptResultsMap,
        setFilterTranscriptResultsMap,

        setStoreTranscriptEmbeddingsQueue,

        filterTranscriptPrompt,
        setFilterTranscriptPrompt,
        filterTranscriptResult,
        filterTranscriptLoading,
        filterTranscriptError,

        filterModelList,
        filterModel,
        setFilterModel,

        homeSystemOptions,
        selectedClassifierOptions,
        setSelectedClassifierOptions,

        applyClassifier,
        setApplyClassifier,

        filterTranscriptWithDecoder,
        isFilteringTranscript,
        errorFilteringTranscript,
        setErrorFilteringTranscript,

        storeFilterResults,
        filterTranscriptResultsStore,
        setFilterTranscriptResultsStore,
      }}>
      {children}
    </CopilotContext.Provider>
  );
}

export function useCopilotContext() {
  return useContext(CopilotContext);
}
